import React, { useEffect, useRef, useState } from 'react';
import { Wrench, Keyboard } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { TagMaintenanceUnlockDialog } from './TagMaintenanceUnlockDialog';

const typing = (el) => !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);

// Typed text ending with an unlock phrase (checked on the server) opens the unlock dialog.
function usePhraseUnlock(inputRef, skipFields) {
  const [text, setText] = useState('');
  const [hit, setHit] = useState(null); // { phrase, full, canFull }
  const wait = useRef(null);
  useEffect(() => {
    if (hit) return undefined;
    const onKey = (e) => {
      if (e.target === inputRef.current || e.ctrlKey || e.metaKey || e.altKey) return;
      if (skipFields && typing(e.target)) return;
      if (e.key === 'Backspace') setText((t) => t.slice(0, -1));
      else if (e.key.length === 1) setText((t) => (t + e.key).slice(-200));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hit, inputRef, skipFields]);
  useEffect(() => {
    clearTimeout(wait.current);
    if (!text.trim()) return undefined;
    const open = (d) => { setHit({ phrase: text, full: !!d.full, canFull: !!d.can_full }); setText(''); inputRef.current?.blur(); };
    const t = setTimeout(() => {
      notesApi.post('/public/portal-unlock/verify', { text }).then(({ data }) => { if (data?.ok) open(data); }).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [text, inputRef]);
  return { text, setText, hit, close: () => setHit(null) };
}

const HiddenInput = ({ inputRef, text, setText }) => (
  <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value.slice(-200))} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false}
    aria-label="Keyboard input" className="fixed bottom-0 left-0 h-px w-px opacity-0" data-testid="tagws-locked-input" />
);


// Locked portal: one maintenance page for everyone; mobile opens the keyboard via the icon.
export function TagLockedScreen() {
  const inputRef = useRef(null);
  const { text, setText, hit, close } = usePhraseUnlock(inputRef, false);
  // The keyboard icon stays hidden until 5 quick taps on the page.
  const [showKb, setShowKb] = useState(false);
  const taps = useRef([]);
  const onTap = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 3000), now];
    if (taps.current.length >= 5) { taps.current = []; setShowKb(true); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-50 px-6" onClick={onTap} data-testid="tagws-portal-locked">
      <div className="max-w-md text-center animate-in fade-in zoom-in-95 duration-500">
        <span className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-2xl bg-white text-slate-500 shadow-sm ring-1 ring-slate-200"><Wrench size={28} /></span>
        <p className="text-xl font-semibold leading-snug text-slate-800 sm:text-2xl" data-testid="tagws-portal-locked-message">The site is under maintenance, We will be back soon.</p>
      </div>
      <HiddenInput inputRef={inputRef} text={text} setText={setText} />
      {showKb ? <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.focus(); }} aria-label="Open keyboard"
        className="fixed bottom-6 right-4 grid h-11 w-11 place-items-center rounded-full text-slate-300 active:scale-95 md:hidden" data-testid="tagws-locked-keyboard-button"><Keyboard size={18} /></button> : null}
      {hit ? <TagMaintenanceUnlockDialog phrase={hit.phrase} onClose={close} /> : null}
    </div>
  );
}

// Sales & Approval mode: typing the phrase anywhere on the main page (outside fields) opens the same unlock dialog.
export function TagPhraseUnlock() {
  const inputRef = useRef(null);
  const { text, setText, hit, close } = usePhraseUnlock(inputRef, true);
  return (
    <>
      <HiddenInput inputRef={inputRef} text={text} setText={setText} />
      {hit ? <TagMaintenanceUnlockDialog phrase={hit.phrase} onClose={close} /> : null}
    </>
  );
}
